<template>
  <!-- S05 · Registro unico de riesgos / incidencias / oportunidades y acciones. -->
  <summary-card :title="$t('prm_summary_s05_title')" :mode="mode" :mark="true" chip="pm,cm,field">
    <template #icon><b-icon pack="fas" icon="shield-halved" size="is-small"></b-icon></template>
    <template #extra><span class="count">{{ $t('prm_summary_s05_note') }} · {{ risks.length }} {{ $t('prm_summary_records') }}</span></template>

    <prm-summary-editable-table
      :rows="orderedRisks"
      :columns="columns"
      :blank="blankRisk"
      :mark="mode"
      :numbered="true"
      :number-label="$t('prm_summary_col_id')"
      :add-label="$t('prm_summary_add_risk')"
      :remove-title="$t('prm_summary_remove_row')"
      :empty-text="$t('prm_summary_no_risks')"
      :reset-key="resetKey"
      :row-class="riskRowClass"
      @change="onChange">
    </prm-summary-editable-table>
  </summary-card>
</template>

<script>
  import SummaryCard from './summary-card.vue'
  import PrmSummaryEditableTable from './editable-table.vue'
  import { RISK_TYPES, RISK_STATUSES, LEVELS, riskScore, scoreColor, userOptions } from './summary-model'

  export default {
    name: 'PrmSummaryS05',

    components: { SummaryCard, PrmSummaryEditableTable },

    props: {
      risks: { type: Array, default: () => [] },
      sources: { type: Object, default: () => ({}) },
      mode: { type: Boolean, default: false },
      resetKey: { type: Number, default: 0 },
    },

    computed: {
      // Responsable: usuarios del proyecto (fuente real, de sources).
      ownerOptions () { return userOptions(this.sources.users) },
      // Orden descendente por score (se aplica al cargar; durante la edición no se reordena).
      orderedRisks () {
        return [...this.risks].sort((a, b) => riskScore(b) - riskScore(a))
      },
      columns () {
        return [
          { key: 'type', label: this.$t('prm_summary_col_type'), type: 'select', align: 'center', options: this.opts(RISK_TYPES, 'prm_summary_risk_type_'), cellClass: (r) => r.type === 'opportunity' ? 'type-opportunity' : 'type-threat' },
          { key: 'description', label: this.$t('prm_summary_col_description'), type: 'text', width: '24%', multiline: true },
          { key: 'impact', label: this.$t('prm_summary_col_impact'), type: 'select', align: 'center', options: this.levelOpts('prm_summary_impact_'), sub: (v) => this.$t('prm_summary_impact_' + v) },
          { key: 'probability', label: this.$t('prm_summary_col_probability'), type: 'select', align: 'center', options: this.levelOpts('prm_summary_probability_'), sub: (v) => this.$t('prm_summary_probability_' + v) },
          // I × P (calculada): impacto × probabilidad. Sin color (la fila ya se tiñe por criticidad).
          { key: 'score', label: this.$t('prm_summary_col_score'), type: 'readonly', align: 'center', format: (r) => riskScore(r) },
          { key: 'action', label: this.$t('prm_summary_col_action'), type: 'text', width: '22%', multiline: true },
          { key: 'owner', label: this.$t('prm_summary_col_owner'), type: 'select', align: 'center', options: this.ownerOptions },
          { key: 'targetDate', label: this.$t('prm_summary_col_target_date'), type: 'date', align: 'center' },
          { key: 'status', label: this.$t('prm_summary_col_status'), type: 'select', align: 'center', options: this.opts(RISK_STATUSES, 'prm_summary_risk_status_'), cellClass: (r) => 'st-badge st-' + (r.status || 'open') },
        ]
      },
      blankRisk () {
        return { type: 'threat', description: '', impact: 1, probability: 1, action: '', owner: '', targetDate: '', status: 'open' }
      },
    },

    methods: {
      opts (codes, prefix) {
        return codes.map((c) => ({ value: c, label: this.$t(prefix + c) }))
      },
      levelOpts (prefix) {
        return LEVELS.map((n) => ({ value: n, label: `${n} - ${this.$t(prefix + n)}` }))
      },
      // Fila coloreada si es crítica y está abierta (misma fórmula): las RIESGOS en rojo y
      // las OPORTUNIDADES en verde.
      riskRowClass (r) {
        if (r.status === 'closed') return ''
        if (scoreColor(riskScore(r)) !== 'red') return ''
        return r.type === 'opportunity' ? 'green' : 'red'
      },
      onChange (rows) {
        this.$emit('save', rows)
      },
    },
  }
</script>

<style lang="scss" scoped>
  .count { margin-left: auto; font-size: 9px; font-weight: 400; text-transform: none; color: #9aa4b2; }
</style>
